import {
  HttpClient,
  type PaginatedResponse,
  robotoHeaders,
} from "@/shared/http/index.ts";
import { parseTimestampsAsBigInt } from "@/shared/json";
import { SearchQueryBody } from "@/types/search";

import {
  MetadataValuesRequest,
  MetadataValuesResponse,
  QualifiedRoboqlQuery,
  QueryRecord,
  SubmitRoboqlQueryRequest,
  SubmitStructuredQueryRequest,
  SubmitTermQueryRequest,
} from "./QueryRecord.ts";

export class QueryService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  async convertNlpToRoboql(
    prompt: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      orgId: string;
    }>,
  ): Promise<QualifiedRoboqlQuery> {
    const url = this.#httpClient.constructUrl("v1/query/convert/nlp-to-roboql");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify({ prompt }),
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<QualifiedRoboqlQuery>();
  }

  /**
   * Parses a RoboQL string into a `SearchQueryBody` without executing
   * it. The returned spec is a **projection** — only fields the user
   * explicitly wrote in the RoboQL string are populated; everything
   * else stays `undefined`.
   *
   * Throws on parse failure (HTTP 400 mapped to a thrown error by
   * `httpClient.post`). Field-name validity is NOT checked here — that
   * happens at execution time, the same as for any other
   * `QuerySpecification`.
   */
  async convertRoboqlToSpec(
    query: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      orgId: string;
    }>,
  ): Promise<SearchQueryBody> {
    const url = this.#httpClient.constructUrl(
      "v1/query/convert/roboql-to-spec",
    );

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify({ query }),
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<SearchQueryBody>();
  }

  async submitStructured(
    request: SubmitStructuredQueryRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<QueryRecord> {
    const url = this.#httpClient.constructUrl("v1/query/submit/structured");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return response.json<QueryRecord>();
  }

  async submitTerm(
    request: SubmitTermQueryRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<QueryRecord> {
    const url = this.#httpClient.constructUrl("v1/query/submit/term");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return response.json<QueryRecord>();
  }

  async submitRoboql(
    request: SubmitRoboqlQueryRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<QueryRecord> {
    const url = this.#httpClient.constructUrl("v1/query/submit/roboql");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return response.json<QueryRecord>();
  }

  async getQueryRecord(
    queryId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<QueryRecord> {
    const url = this.#httpClient.constructUrl(`v1/query/id/${queryId}`);

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });

    return response.json<QueryRecord>();
  }

  async getQueryResultCount(
    queryId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<number> {
    const url = this.#httpClient.constructUrl(`v1/query/id/${queryId}/count`);

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });

    return Number.parseInt(await response.text());
  }

  async getQueryResults<T>(
    queryId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      nextPageToken: string;
      pageSize: number;
    }>,
  ): Promise<PaginatedResponse<T>> {
    const searchParams = new URLSearchParams();

    if (options?.nextPageToken !== undefined) {
      searchParams.append("next_page_token", options.nextPageToken);
    }

    if (options?.pageSize !== undefined) {
      searchParams.append("page_size", options.pageSize.toString());
    }

    const url = this.#httpClient.constructUrl(
      `v1/query/id/${queryId}/results`,
      searchParams,
    );

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });

    // Note: 'parseTimestampsAsBigInt' will convert any 'start_time' and
    // 'end_time' fields to BigInt. 'created' and 'modified' are not affected.
    return response.json<PaginatedResponse<T>>(parseTimestampsAsBigInt);
  }

  async queryMetadataValues(
    request: MetadataValuesRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<MetadataValuesResponse> {
    const url = this.#httpClient.constructUrl("v1/query/metadata/values");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return response.json<MetadataValuesResponse>();
  }
}
